import * as React from "react";
import { Route, RouteProps, Routes } from "react-router-dom";
import { Actions, PrivilegeLevels, Resources, defineUserPriv } from "tools/privilegeChecker";
import ProtectedRoute from "./ProtectedRoute";
import { PrivDependantRoute } from "./PrivDependantRoute";
import { Settings } from "pages/settings";
import { UserGroupsPage } from "pages/userGroups";
import { CompanyHierarchyPage } from "pages/companyHierarchy";
import {
  ReportIntegrationsListPage,
  ReportIntegrationFormPage,
} from "pages/reportIntegrations";
import { PassportReturnsPage } from "pages/passportReturns";
import { AmountIntegrityPage } from "pages/amountIntegrity";
import UserRoutes from "./userRoutes";
import CompaniesRoutes from "./companiesRoutes";
import DisbursementAccountRoutes from "./disbursementAccountRoutes";
import { Navigate } from "react-router-dom";

const OwnerOnlyRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const isOwner = defineUserPriv() === PrivilegeLevels.OWNER;
  if (!isOwner) {
    return <Navigate to="/settings" />;
  }
  return children;
};

interface ISettingsRouteProps extends RouteProps {
  isAuth: boolean;
  userPriv: number;
}

const SettingsRoutes: React.FunctionComponent<ISettingsRouteProps> = (
  props
) => {
  const { isAuth, userPriv } = props;
  return (
    <Routes>
      <Route path="/" element={<Settings />} />
      <Route
        path="/user-groups"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <UserGroupsPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-hierarchy"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <CompanyHierarchyPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/report-integrations"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <ReportIntegrationsListPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/report-integrations/new"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <ReportIntegrationFormPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/report-integrations/:id"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <ReportIntegrationFormPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport-returns"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <PassportReturnsPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/amount-integrity"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <OwnerOnlyRoute>
              <AmountIntegrityPage />
            </OwnerOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/*"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <PrivDependantRoute
              requirements={{ action: Actions.WRITE, resource: Resources.USER }}
            >
              <UserRoutes />
            </PrivDependantRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/*"
        element={
          <ProtectedRoute isAuth={isAuth}>
            <PrivDependantRoute
              requirements={{
                action: Actions.READ,
                resource: Resources.COMPANY,
              }}
            >
              <CompaniesRoutes userPriv={userPriv} />
            </PrivDependantRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disbursementAccounts/*"
        element={
          <PrivDependantRoute
            requirements={[
              {
                action: Actions.READ,
                resource: Resources.DISBURSEMENT_ACCOUNTS,
              },
              {
                action: Actions.READ,
                resource: Resources.RECEIVABLES_ACCOUNTS,
              },
            ]}
          >
            <DisbursementAccountRoutes />
          </PrivDependantRoute>
        }
      />
    </Routes>
  );
};

export default SettingsRoutes;
